import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  Fragment,
  Host,
  Listen,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import debounce from 'debounce';

interface UiCmsHeaderBody {
  alignment: {
    serviceArea: 'bottom-right' | 'top-right';
    navigation: 'bottom-left' | 'top-left';
    logos: 'left' | 'space-between';
  };
  logos: {
    primary: {
      alt: string;
      href?: string;
      src: string;
      srcMobile?: string;
      height?: number;
      heightMobile?: number;
    };
    secondary?: {
      alt: string;
      href?: string;
      src: string;
      srcMobile?: string;
      height?: number;
      heightMobile?: number;
    };
  };
}

export interface HeaderHeightChangeEvent {
  height: number;
}

interface Refs {
  serviceAreaTop: HTMLElement;
  serviceAreaBottom: HTMLElement;
  navigationTop: HTMLElement;
  navigationBottom: HTMLElement;
  sdHeader: HTMLElement;
  sdHeaderContent: HTMLElement;
  primaryLogo: HTMLElement;
  secondaryLogoLeft?: HTMLElement;
  secondaryLogoRight?: HTMLElement;
}

@Component({
  tag: 'ui-cms-header',
  styleUrls: { default: 'ui-cms-header.scss', editing: 'ui-cms-header.magnolia.scss' },
  shadow: true,
})
export class UiCmsHeader implements ComponentInterface {
  @Element() el: HTMLUiHeaderElement;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) body: UiCmsHeaderBody;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop({ mutable: true }) i18n = {
    navigationName: 'Hauptnavigation',
    logoAriaLabel: 'Union Investment Logo',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Event() primaryLogoClick: EventEmitter<void>;

  // forward to ui-cms-navigation -> ui-cms-navigation-element AND ui-cms-offcanvas-navigation --> <a>
  // allows default navigation on menu item click if TRUE
  // prevents default navigation on menu item click and emits triggerExternalNavigation event for external callback if FALSE
  @Prop({ attribute: 'navigateOnClick' }) navigateOnClick = true;

  @Listen('offcanvasInit')
  onOffcanvasInit() {
    this.initOffcanvas();
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
    this.onWindowResize();
  }

  private refs: Refs = {
    serviceAreaTop: null,
    serviceAreaBottom: null,
    navigationTop: null,
    navigationBottom: null,
    sdHeader: null,
    sdHeaderContent: null,
    primaryLogo: null,
  };

  private hasSecondaryLogo = false;

  private hasNavigation = false;

  private hasServiceArea = false;

  private resizeObserver: ResizeObserver;

  private currentHeaderHeight = 0;

  private offCanvasNavigation: HTMLUiOffcanvasPanelElement;

  private navigationMobileButton: HTMLButtonElement;

  @State() collidedNavigationAndServiceArea = false;

  @State() isMobile = false;

  @State() offcanvasNavBody = null;

  @State() serviceAreaBody = null;

  @Event({ eventName: 'uiCmsHeaderHeightChanged' })
  uiCmsHeaderHeightChangedEvent: EventEmitter<HeaderHeightChangeEvent>;

  @Event({ eventName: 'offcanvasNavigationMenuOpened' }) offcanvasNavigationMenuOpened: EventEmitter;

  @Listen('resize', { target: 'window' })
  onWindowResize() {
    this.isMobile = window.innerWidth < 1024;
  }

  /**
   * Listening for ui-sticky component event, as header is fixed by style
   * ui-sticky has to consider it while calculator its position.
   */
  @Listen('uiStickyComponentIsReady', { target: 'window' })
  onStickyComponentReady(e) {
    const uiStickyElement = e.target as HTMLUiStickyElement;
    uiStickyElement.addElementToCalculation([this.refs.sdHeader]);
  }

  @Listen('offcanvasTrigger')
  onOffcanvasTrigger(event: CustomEvent<{ id: string }>) {
    const { id } = event.detail;

    if (id !== 'offcanvas-navigation') {
      return;
    }

    const isOpen = this.offCanvasNavigation.hasAttribute('open');

    if (isOpen) {
      this.navigationMobileButton?.focus();
    } else {
      this.offcanvasNavigationMenuOpened.emit();
    }
  }

  componentDidLoad() {
    this.refs.sdHeaderContent = this.refs.sdHeader.shadowRoot.querySelector('header div');
    this.hasSecondaryLogo = !!this.body.logos.secondary?.src;
    this.hasNavigation = !!this.el.querySelector('[slot="navigation"]');
    this.hasServiceArea = !!this.el.querySelector('[slot="service-area"]');
    this.addResizeObserver();
    this.setLogoHeights();
    this.checkIfNavigationAndServiceAreaCollide();
    this.initOffcanvas();

    // synchronize offcanvas and standard navigation
    // if CMS navigation element is present in navigation slot, pass navigateOnClick prop down to standard navigation
    const cmsNavigationElement = this.el.querySelector('ui-cms-navigation[slot="navigation"]');
    cmsNavigationElement?.setAttribute('navigateOnClick', this.navigateOnClick.toString());
  }

  componentDidUpdate(): void {
    // is needed because of the first rerender
    // afterwards gets updated by resize observer
    if (this.hasNavigation) this.setNavigationFlyoutWidth();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        if (window.innerWidth >= 1024) {
          if (this.hasNavigation && this.hasServiceArea) this.checkIfNavigationAndServiceAreaCollide();
          if (this.hasNavigation) this.setNavigationFlyoutWidth();
        }
      }, 100)
    );

    this.resizeObserver.observe(this.refs.sdHeader);
  }

  private checkIfNavigationAndServiceAreaCollide(): void {
    const { navigation, serviceArea } = this.body.alignment;
    const bothBottom = navigation === 'bottom-left' && serviceArea === 'bottom-right';
    const bothTop = navigation === 'top-left' && serviceArea === 'top-right';

    if (bothBottom || bothTop) {
      const nav = this.refs.navigationBottom || this.refs.navigationTop;
      const service = this.refs.serviceAreaBottom || this.refs.serviceAreaTop;
      const navigationWidth = parseFloat(getComputedStyle(nav).width);
      const serviceAreaWidth = parseFloat(getComputedStyle(service).width);
      const headerContentWidth = parseFloat(getComputedStyle(this.refs.sdHeaderContent).width);
      const primaryLogoWidth = this.refs.primaryLogo?.offsetWidth || 0;
      const secondaryLogo = this.refs.secondaryLogoLeft || this.refs.secondaryLogoRight;
      const secondaryLogoWidth = secondaryLogo?.offsetWidth || 0;
      const dividerWidth = this.body.alignment.logos === 'left' && this.hasSecondaryLogo ? 65 : 0;
      const availableSpace = bothTop
        ? headerContentWidth - primaryLogoWidth - secondaryLogoWidth - dividerWidth
        : headerContentWidth;

      this.collidedNavigationAndServiceArea = navigationWidth + serviceAreaWidth >= availableSpace;
    }
  }

  private setNavigationFlyoutWidth(): void {
    const flyoutElements: NodeListOf<HTMLElement> = this.el
      .querySelector('ui-cms-navigation')
      .querySelectorAll('.mega-menu');

    flyoutElements.forEach((item) => {
      const elem = item;
      elem.style.width = `${this.calcFlyoutWidth()}px`;
    });
  }

  private calcFlyoutWidth(): number {
    const nav = this.refs.navigationBottom || this.refs.navigationTop;
    const headerRightPadding = parseInt(window.getComputedStyle(this.refs.sdHeaderContent).paddingRight, 10);
    const endHeader = this.refs.sdHeaderContent.getBoundingClientRect().right - headerRightPadding;
    const startNavigation = nav.getBoundingClientRect().left;

    return endHeader - startNavigation;
  }

  private setLogoHeights() {
    if (this.body.logos?.primary?.height)
      this.el.style.setProperty('--header-logo-primary-height', `${this.body.logos.primary.height}px`);
    if (this.body.logos?.primary?.heightMobile)
      this.el.style.setProperty('--header-logo-primary-mobile-height', `${this.body.logos.primary.heightMobile}px`);
    if (this.body.logos?.secondary?.height)
      this.el.style.setProperty('--header-logo-secondary-height', `${this.body.logos.secondary.height}px`);
    if (this.body.logos?.secondary?.heightMobile)
      this.el.style.setProperty('--header-logo-secondary-mobile-height', `${this.body.logos.secondary.heightMobile}px`);
  }

  // this.el.querySelector is used as a fallback in case the component is encapsulated in a shadow dom
  private initOffcanvas = () => {
    this.offcanvasNavBody =
      document.querySelector('ui-cms-navigation')?.body || this.el.querySelector('ui-cms-navigation')?.body;
    this.serviceAreaBody =
      document.querySelector('ui-cms-header-service-area')?.body ||
      this.el.querySelector('ui-cms-header-service-area')?.body;
  };

  private hasOffcanvasFunctionalities = () => this.isMobile && (this.hasServiceArea || this.hasNavigation);

  private trackLogoClick = () => {
    const currentUrl = window.location.origin;
    const isRelativeUrl = this.body.logos.primary.href.startsWith('/') || this.body.logos.primary.href.startsWith('#');
    const target = isRelativeUrl ? `${currentUrl}${this.body.logos.primary.href}` : this.body.logos.primary.href;
    const event: ActionTrackingEvent = {
      eventInfo: {
        eventType: 'Action',
        eventName: 'Primary Logo Click',
      },
      category: {
        area: 'Header',
      },
      attributes: {
        elementLabel: 'Logo',
        target,
        elementType: 'Link',
      },
    };
    window.ui?.services?.tracking?.track(event);
  };

  render() {
    const renderLogo = (classification: 'primary' | 'secondary') => (
      <Fragment>
        <img src={this.body.logos[classification].src} alt={this.body.logos[classification].alt} />
        {this.body.logos[classification].srcMobile && (
          <img src={this.body.logos[classification].srcMobile} alt={this.body.logos[classification].alt} />
        )}
      </Fragment>
    );

    const renderPrimaryLogo = () => {
      if (this.navigateOnClick) {
        return this.body?.logos.primary.href ? (
          <sd-link
            class="primary-logo"
            ref={(el) => {
              this.refs.primaryLogo = el;
            }}
            href={this.body?.logos.primary.href}
            onClick={() => this.trackLogoClick()}
            standalone
            aria-label={this.i18n.logoAriaLabel}
          >
            {renderLogo('primary')}
          </sd-link>
        ) : (
          <span
            class="primary-logo"
            ref={(el) => {
              this.refs.primaryLogo = el;
            }}
          >
            {renderLogo('primary')}
          </span>
        );
      }

      return (
        <span
          class="primary-logo pointer"
          ref={(el) => {
            this.refs.primaryLogo = el;
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            this.primaryLogoClick.emit();
          }}
        >
          {renderLogo('primary')}
        </span>
      );
    };

    const renderSecondaryLogo = (refName: keyof Refs) =>
      this.body?.logos.secondary?.src &&
      (this.body?.logos.secondary.href ? (
        <sd-link
          class="secondary-logo"
          href={this.body?.logos.secondary.href}
          ref={(el) => {
            this.refs[refName] = el;
          }}
        >
          {renderLogo('secondary')}
        </sd-link>
      ) : (
        <span
          class="secondary-logo"
          ref={(el) => {
            this.refs[refName] = el;
          }}
        >
          {renderLogo('secondary')}
        </span>
      ));

    const renderServiceArea = (refName: keyof Refs) => (
      <div
        ref={(el) => {
          this.refs[refName] = el;
        }}
        class={{
          'service-area-wrapper': true,
          'service-area-wrapper--flipped': this.collidedNavigationAndServiceArea,
          'service-area-wrapper--centered':
            this.body.alignment.serviceArea === 'top-right' && !this.hasSecondaryLogo && !this.hasNavigation,
        }}
      >
        <div class="service-area">
          <slot name="service-area" />
        </div>
      </div>
    );

    const renderNavigation = (refName: keyof Refs) => (
      <div
        class={{
          'navigation-wrapper': true,
          'navigation-wrapper--row': this.body.alignment.navigation === 'top-left',
        }}
      >
        <nav
          aria-label={this.i18n.navigationName}
          class="navigation--desktop"
          ref={(el) => {
            this.refs[refName] = el;
          }}
        >
          <slot name="navigation"></slot>
        </nav>
      </div>
    );

    const renderMobileNavigationTrigger = () => (
      <div class="offcanvas-trigger">
        <ui-offcanvas-trigger offcanvas-id="offcanvas-navigation">
          <button
            class="off-canvas-toggle"
            ref={(el) => {
              this.navigationMobileButton = el;
            }}
          >
            Navigation
          </button>
        </ui-offcanvas-trigger>
      </div>
    );

    const renderMobileSearchTrigger = () => (
      <div class="search-trigger">
        <ui-offcanvas-trigger offcanvas-id="offcanvas-mobile-search">
          <button aria-label="Open search">
            <ui-icon aria-hidden="true" icon="magnifying-glass" type="system"></ui-icon>
          </button>
        </ui-offcanvas-trigger>
      </div>
    );

    return (
      <Host class="ui-cms-header">
        <sd-header
          fixed
          ref={(el) => {
            this.refs.sdHeader = el;
          }}
        >
          <div class="top">
            <div class="top__left">
              {renderPrimaryLogo()}
              {this.body?.alignment.logos === 'left' && this.body?.logos.secondary?.src && (
                <Fragment>
                  <div class="divider"></div>
                  {renderSecondaryLogo('secondaryLogoLeft')}
                </Fragment>
              )}
              {this.body?.alignment.navigation === 'top-left' &&
                !this.collidedNavigationAndServiceArea &&
                renderNavigation('navigationTop')}
            </div>
            <div class="top__right">
              {(this.body?.alignment.serviceArea === 'top-right' || this.collidedNavigationAndServiceArea) &&
                renderServiceArea('serviceAreaTop')}
              {this.body?.alignment.logos === 'space-between' && renderSecondaryLogo('secondaryLogoRight')}
              {this.isMobile &&
                (this.serviceAreaBody?.search || this.serviceAreaBody?.algoliaSearchTargetUrl) &&
                renderMobileSearchTrigger()}
              {this.hasOffcanvasFunctionalities() && renderMobileNavigationTrigger()}
            </div>
          </div>
          <div class="bottom">
            <div class="bottom__left">
              {(this.body?.alignment.navigation === 'bottom-left' || this.collidedNavigationAndServiceArea) &&
                renderNavigation('navigationBottom')}
            </div>
            <div class="bottom__right">
              {this.body?.alignment.serviceArea === 'bottom-right' &&
                !this.collidedNavigationAndServiceArea &&
                renderServiceArea('serviceAreaBottom')}
            </div>
          </div>
        </sd-header>
        {this.hasOffcanvasFunctionalities() && (
          <ui-offcanvas-panel
            id="offcanvas-navigation"
            position="right"
            ref={(el) => {
              this.offCanvasNavigation = el;
            }}
          >
            <ui-cms-offcanvas-navigation
              navigateOnClick={this.navigateOnClick}
              body={this.offcanvasNavBody}
              serviceArea={this.serviceAreaBody}
            ></ui-cms-offcanvas-navigation>
          </ui-offcanvas-panel>
        )}
        {this.isMobile && (this.serviceAreaBody?.search || this.serviceAreaBody?.algoliaSearchTargetUrl) && (
          <ui-offcanvas-panel id="offcanvas-mobile-search" position="right">
            <ui-cms-offcanvas-mobile-search
              serviceArea={this.serviceAreaBody}
              siteTitle={this.offcanvasNavBody?.site?.title}
            ></ui-cms-offcanvas-mobile-search>
          </ui-offcanvas-panel>
        )}
      </Host>
    );
  }
}
